import React, { useEffect, useState } from 'react'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import toast from 'react-hot-toast'
import { brandingDraftFromTenant, brandingImageSrc } from './tenantShared'

const BRANDING_FILE_ACCEPT = 'image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml'

function hexPreview(hex) {
  if (!hex || !String(hex).trim()) return null
  let h = String(hex).trim()
  if (h.startsWith('#')) h = h.slice(1)
  if (h.length === 3 && /^[0-9a-fA-F]{3}$/i.test(h)) {
    h = h.split('').map((c) => c + c).join('')
  }
  if (!/^[0-9a-fA-F]{6}$/i.test(h)) return null
  return `#${h.toLowerCase()}`
}

export default function TenantBrandingTab({ tenant, onTenantUpdated }) {
  const groupId = tenant?.group_id
  const [draft, setDraft] = useState(brandingDraftFromTenant(tenant || {}))
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(null)

  useEffect(() => {
    if (tenant) setDraft(brandingDraftFromTenant(tenant))
  }, [tenant])

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }))

  const save = async () => {
    setSaving(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logoUrl: draft.logoUrl,
          brandmarkUrl: draft.brandmarkUrl,
          primaryColor: draft.primaryColor,
          secondaryColor: draft.secondaryColor,
          highlightColor: draft.highlightColor,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success('Branding saved')
      onTenantUpdated?.()
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const uploadAsset = async (kind, file) => {
    if (!file) return
    setUploading(kind)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await authFetch(
        getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}/branding/upload?kind=${encodeURIComponent(kind)}`),
        { method: 'POST', body: fd }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      if (data.url) {
        if (kind === 'brandmark') setField('brandmarkUrl', data.url)
        else setField('logoUrl', data.url)
        toast.success('Uploaded')
      }
    } catch (e) {
      toast.error(e.message || 'Upload failed')
    } finally {
      setUploading(null)
    }
  }

  if (!tenant) return null

  return (
    <div className="max-w-4xl space-y-6">
      <p className="text-sm text-gray-600">Logo, brandmark, and theme colours for this tenant&apos;s customer apps.</p>
      {['logo', 'brandmark'].map((kind) => {
        const urlKey = kind === 'logo' ? 'logoUrl' : 'brandmarkUrl'
        const label = kind === 'logo' ? 'Logo' : 'Brandmark'
        return (
          <div key={kind} className="rounded-lg border bg-white p-4">
            <p className="font-semibold text-gray-800 mb-2">{label}</p>
            <input
              className="w-full rounded-md border px-3 py-2 text-sm font-mono mb-2"
              value={draft[urlKey]}
              onChange={(e) => setField(urlKey, e.target.value)}
            />
            <label className="inline-flex cursor-pointer items-center rounded-md border px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
              {uploading === kind ? 'Uploading…' : 'Upload file'}
              <input
                type="file"
                accept={BRANDING_FILE_ACCEPT}
                className="sr-only"
                disabled={uploading === kind}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) uploadAsset(kind, f)
                  e.target.value = ''
                }}
              />
            </label>
            {draft[urlKey]?.trim() && (
              <img
                src={brandingImageSrc(draft[urlKey].trim(), groupId, kind)}
                alt=""
                className="mt-3 max-h-20 object-contain"
              />
            )}
          </div>
        )
      })}
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { key: 'primaryColor', label: 'Primary' },
          { key: 'secondaryColor', label: 'Secondary' },
          { key: 'highlightColor', label: 'Highlight' },
        ].map(({ key, label }) => {
          const pv = hexPreview(draft[key])
          return (
            <label key={key} className="block text-sm">
              <span className="font-medium text-gray-700">{label}</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="color"
                  className="h-9 w-10 rounded border"
                  value={pv || '#000000'}
                  onChange={(e) => setField(key, e.target.value)}
                />
                <input
                  className="flex-1 rounded-md border px-3 py-2 text-sm font-mono"
                  value={draft[key]}
                  onChange={(e) => setField(key, e.target.value)}
                />
              </div>
            </label>
          )
        })}
      </div>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save branding'}
      </button>
    </div>
  )
}
